import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Play, Pause, Volume2, BookOpen, Presentation,
  CircleHelp, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  Headphones, Loader2, Sparkles, RotateCcw, Film, ExternalLink, Globe, Mic,
} from "lucide-react";
import { apiGet, apiPost, API } from "../lib/api";
import VideoPlayerPremium from "../components/VideoPlayerPremium";
import BlocksLessonView from "../components/LessonBlocksRenderer";

// Bilingual UI strings — same single-source-of-truth pattern used by VIP.jsx
// All visible chrome flips when the user toggles language at /vip top-level.
const UI = {
  it: {
    back: "Torna ai moduli", lesson: "Lezione",
    tab_video: "Video", tab_read: "Leggi", tab_slides: "Slide", tab_quiz: "Quiz",
    video_empty: "Nessun video disponibile per questa lezione.",
    video_empty_hint: "Il video sarà aggiunto dal team Academia.",
    audio_label: "Ascolta", audio_unavailable: "Audio non disponibile",
    audio_generating: "Audio in preparazione…",
    video_label: "Video lezione", video_open_external: "Apri il video",
    summary: "Sintesi", marked_read: "Letta",
    mark_read: "Segna come letta", marked_slides: "Slide completate",
    mark_slides: "Segna slide come complete",
    slide_of: "Slide", slide_next: "Successiva",
    slide_prev: "Precedente",
    quiz_intro: "Verifica la tua comprensione con queste domande.",
    select_answer: "Seleziona una risposta",
    submit: "Conferma", correct: "Corretto", wrong: "Sbagliato",
    explanation: "Spiegazione",
    score_label: "Punteggio",
    next_question: "Prossima domanda", finish_quiz: "Termina quiz",
    quiz_done: "Quiz completato",
    practice_oral: "Pratica adesso all'orale",
    practice_oral_hint: "Apri la simulazione orale con questa domanda pre-selezionata",
    progress_complete: "Lezione completata",
    progress_keep: "Continua a studiare",
    not_found: "Lezione non trovata",
    retry: "Riprova",
    prev_lesson: "Lezione precedente",
    next_lesson: "Lezione successiva",
    lesson_of: "di",
    upgrade_eyebrow: "Lezione gratuita completata",
    upgrade_title: "Sblocca il resto del Modulo 1 e l'intera Academia VIP",
    upgrade_desc: "Hai appena visto solo una delle 5 lezioni del modulo. Continua il tuo percorso con audio bilingue, illustrazioni anatomiche premium e quiz orientati all'esame ministeriale.",
    upgrade_cta: "Accedi all'Academia VIP",
    lang_toggle_label: "Cambia lingua dei contenuti",
  },
  es: {
    back: "Volver a los módulos", lesson: "Lección",
    tab_video: "Video", tab_read: "Lee", tab_slides: "Slides", tab_quiz: "Quiz",
    video_empty: "No hay video disponible para esta lección.",
    video_empty_hint: "El video será agregado por el equipo Academia.",
    audio_label: "Escucha", audio_unavailable: "Audio no disponible",
    audio_generating: "Audio en preparación…",
    video_label: "Video de la lección", video_open_external: "Abrir el video",
    summary: "Resumen", marked_read: "Leída",
    mark_read: "Marcar como leída", marked_slides: "Slides completados",
    mark_slides: "Marcar slides como completos",
    slide_of: "Slide", slide_next: "Siguiente", slide_prev: "Anterior",
    quiz_intro: "Verifica tu comprensión con estas preguntas.",
    select_answer: "Selecciona una respuesta",
    submit: "Confirmar", correct: "Correcto", wrong: "Incorrecto",
    explanation: "Explicación",
    score_label: "Puntuación",
    next_question: "Siguiente pregunta", finish_quiz: "Terminar quiz",
    quiz_done: "Quiz completado",
    practice_oral: "Practicá ahora en el oral",
    practice_oral_hint: "Abre la simulación oral con esta pregunta pre-seleccionada",
    progress_complete: "Lección completada",
    progress_keep: "Sigue estudiando",
    not_found: "Lección no encontrada",
    retry: "Reintentar",
    prev_lesson: "Lección anterior",
    next_lesson: "Lección siguiente",
    lesson_of: "de",
    upgrade_eyebrow: "Lección gratuita completada",
    upgrade_title: "Desbloqueá el resto del Módulo 1 y toda la Academia VIP",
    upgrade_desc: "Acabás de ver solo una de las 5 lecciones del módulo. Continuá tu recorrido con audio bilingüe, ilustraciones anatómicas premium y cuestionarios orientados al examen ministerial.",
    upgrade_cta: "Acceder a la Academia VIP",
    lang_toggle_label: "Cambiar idioma de los contenidos",
  },
};

// Lavender palette — kept inline so we don't pollute the global Tailwind
// theme used by the rest of the (italian editorial) app. Phase-2 spec.
const PALETTE = {
  bg: "linear-gradient(135deg, #FAF7FF 0%, #F1ECFF 100%)",
  card: "#FFFFFF",
  primary: "#7B5CE0",        // soft indigo-purple
  primaryDark: "#5A3FC0",
  primarySoft: "#EDE7FF",
  accent: "#FFB8D6",         // pink accent for progress
  text: "#2A1F4A",
  textSoft: "#6E6688",
  border: "#E8E1FA",
  success: "#48B26B",
  danger: "#E25C7E",
};

// ------- UTILITIES -------
const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// ------- VIDEO PLAYER -------
// The dedicated `<VideoPlayerPremium/>` component (in /components) is the
// canonical implementation now — it handles uploads, embeds, watermarks,
// anti-piracy and skeletons. The legacy in-file `VideoPlayer` was removed.

// ------- AUDIO PLAYER -------
function AudioPlayer({ src, label, status }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [duration, setDuration] = useState(0);
  // Playback speed: cycle through 1x → 1.25x → 1.5x → back to 1x. Spec'd
  // intentionally minimal (3 steps) — the user wanted no over-engineering.
  const SPEED_STEPS = [1, 1.25, 1.5];
  const [speedIdx, setSpeedIdx] = useState(0);
  const speed = SPEED_STEPS[speedIdx];

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setT(a.currentTime);
    const onLoad = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  // Apply playback rate whenever the speed changes or the source reloads.
  useEffect(() => {
    const a = audioRef.current;
    if (a) a.playbackRate = speed;
  }, [speed, src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const cycleSpeed = () => setSpeedIdx((i) => (i + 1) % SPEED_STEPS.length);

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * duration;
  };

  if (status === "generating") {
    return (
      <div data-testid="vip-audio-generating"
           className="flex items-center gap-3 px-5 py-4 rounded-2xl"
           style={{ background: PALETTE.primarySoft, color: PALETTE.text }}>
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">{label}</span>
      </div>
    );
  }
  if (!src || status === "error" || status === "skipped") {
    return (
      <div data-testid="vip-audio-unavailable"
           className="flex items-center gap-3 px-5 py-4 rounded-2xl text-sm"
           style={{ background: PALETTE.primarySoft, color: PALETTE.textSoft }}>
        <Volume2 className="h-5 w-5" />
        <span>{label}</span>
      </div>
    );
  }

  const pct = duration ? (t / duration) * 100 : 0;
  return (
    <div data-testid="vip-audio-player"
         className="rounded-2xl p-5 shadow-lg"
         style={{ background: `linear-gradient(135deg, ${PALETTE.primary}, ${PALETTE.primaryDark})`, color: "white" }}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          data-testid="vip-audio-play-btn"
          className="h-14 w-14 rounded-full grid place-items-center transition-transform hover:scale-105"
          style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(12px)" }}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Headphones className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.18em] opacity-80">{label}</span>
          </div>
          <div onClick={seek} className="relative h-2 rounded-full cursor-pointer"
               style={{ background: "rgba(255,255,255,0.22)" }}>
            <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-100"
                 style={{ width: `${pct}%`, background: "white" }} />
          </div>
          <div className="flex justify-between text-[11px] mt-1.5 opacity-80 tabular-nums">
            <span>{formatTime(t)}</span><span>{formatTime(duration)}</span>
          </div>
        </div>
        {/* Speed cycle button: 1x → 1.25x → 1.5x */}
        <button
          onClick={cycleSpeed}
          data-testid="vip-audio-speed-btn"
          aria-label={`Velocidad ${speed}x`}
          className="h-12 min-w-[3.5rem] px-3 rounded-full grid place-items-center transition-all hover:scale-105 text-sm font-semibold tabular-nums"
          style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(12px)" }}
        >
          {speed === 1 ? "1x" : `${speed}x`}
        </button>
      </div>
    </div>
  );
}

// ------- TABS -------
// ===== VideoTab — premium dedicated section for the lesson video =====
// Now delegates ALL rendering to <VideoPlayerPremium/> which handles
// glassmorphism, skeleton loader, watermark, anti-piracy hooks and
// language-mismatch hint. Empty state is kept here so the navigation
// layout stays consistent across lessons.
function VideoTab({ lesson, t, language }) {
  const video = lesson.video;
  if (video) {
    return (
      <div data-testid="vip-tab-content-video" className="space-y-3">
        <VideoPlayerPremium
          video={video}
          language={language}
          lessonId={lesson.lesson_id}
        />
      </div>
    );
  }
  // Empty state — kept minimal, matches the lesson card aesthetic.
  return (
    <div data-testid="vip-tab-content-video-empty"
         className="flex flex-col items-center justify-center py-12 text-center">
      <div className="h-14 w-14 rounded-2xl grid place-items-center mb-4"
           style={{ background: `${PALETTE.primary}14` }}>
        <Film className="h-7 w-7" style={{ color: PALETTE.primary }} />
      </div>
      <p className="font-medium" style={{ color: PALETTE.text }}>
        {t.video_empty}
      </p>
      <p className="text-sm mt-1" style={{ color: PALETTE.muted }}>
        {t.video_empty_hint}
      </p>
    </div>
  );
}

function Tabs({ value, onChange, items }) {
  return (
    <div className="inline-flex gap-1 p-1 rounded-2xl mb-6" style={{ background: PALETTE.primarySoft }}>
      {items.map((it) => {
        const active = it.value === value;
        const Icon = it.icon;
        return (
          <button
            key={it.value}
            onClick={() => onChange(it.value)}
            data-testid={`vip-tab-${it.value}`}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: active ? "white" : "transparent",
              color: active ? PALETTE.primaryDark : PALETTE.textSoft,
              boxShadow: active ? "0 4px 12px rgba(123,92,224,0.18)" : "none",
            }}
          >
            <Icon className="h-4 w-4" />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ------- READ TAB -------
function ReadTab({ lesson, t, onMarkRead }) {
  const paragraphs = useMemo(
    () => (lesson.body || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
    [lesson.body]
  );
  return (
    <div data-testid="vip-tab-content-read" className="space-y-5">
      {lesson.summary ? (
        <div className="rounded-2xl p-5"
             style={{ background: PALETTE.primarySoft, borderLeft: `4px solid ${PALETTE.primary}` }}>
          <p className="text-[11px] uppercase tracking-[0.2em] mb-1.5" style={{ color: PALETTE.primary }}>
            {t.summary}
          </p>
          <p className="leading-relaxed" style={{ color: PALETTE.text }}>{lesson.summary}</p>
        </div>
      ) : null}
      <article className="space-y-4 leading-[1.75]" style={{ color: PALETTE.text, fontSize: "16px" }}>
        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </article>
      <div className="pt-2">
        <button
          onClick={onMarkRead}
          data-testid="vip-mark-read-btn"
          disabled={lesson.progress?.read}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all"
          style={{
            background: lesson.progress?.read ? PALETTE.success : PALETTE.primary,
            color: "white",
            opacity: lesson.progress?.read ? 0.85 : 1,
            cursor: lesson.progress?.read ? "default" : "pointer",
          }}
        >
          <CheckCircle2 className="h-4 w-4" />
          {lesson.progress?.read ? t.marked_read : t.mark_read}
        </button>
      </div>
    </div>
  );
}

// ------- SLIDES TAB -------
function SlidesTab({ lesson, t, onMarkSlidesDone }) {
  const slides = lesson.slides || [];
  const [idx, setIdx] = useState(0);
  if (!slides.length) return null;
  const s = slides[idx];
  const last = idx === slides.length - 1;
  // Relative url_path (/api/...) → absolute via backend URL helper.
  const imgSrc = s.image_url ? `${API}${s.image_url.replace(/^\/api/, "")}` : null;
  const handleNext = () => {
    if (last) { onMarkSlidesDone(); }
    else setIdx(idx + 1);
  };
  return (
    <div data-testid="vip-tab-content-slides" className="space-y-4">
      <div className="flex items-center justify-between text-xs" style={{ color: PALETTE.textSoft }}>
        <span>{t.slide_of} {idx + 1} / {slides.length}</span>
        {lesson.progress?.slides_done ? (
          <span className="inline-flex items-center gap-1 font-medium" style={{ color: PALETTE.success }}>
            <CheckCircle2 className="h-3.5 w-3.5" /> {t.marked_slides}
          </span>
        ) : null}
      </div>
      <div className="flex gap-1.5 mb-2">
        {slides.map((_, i) => (
          <div key={i} className="h-1 flex-1 rounded-full transition-colors"
               style={{ background: i <= idx ? PALETTE.primary : PALETTE.border }} />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={idx}
          data-testid={`vip-slide-${idx}`}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
          className="rounded-2xl p-7 sm:p-10 min-h-[340px] shadow-lg flex flex-col"
          style={{
            background: `linear-gradient(135deg, white 0%, ${PALETTE.primarySoft} 100%)`,
            border: `1px solid ${PALETTE.border}`,
          }}>
          <h3 className="font-serif text-2xl sm:text-3xl mb-6 leading-tight"
              style={{ color: PALETTE.primaryDark }}>
            {s.title}
          </h3>
          {imgSrc && (
            <figure className="mb-6 -mx-2 sm:mx-0" data-testid={`vip-slide-${idx}-image`}>
              <div className="rounded-2xl overflow-hidden bg-white shadow-sm border"
                   style={{ borderColor: PALETTE.border, aspectRatio: "1 / 1", maxWidth: "440px", marginInline: "auto" }}>
                <img
                  src={imgSrc}
                  alt={s.image_alt || s.title}
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              {s.image_alt && (
                <figcaption className="text-xs italic text-center mt-2" style={{ color: PALETTE.textSoft }}>
                  {s.image_alt}
                </figcaption>
              )}
            </figure>
          )}
          <ul className="space-y-3 flex-1">
            {(s.bullets || []).map((b, i) => (
              <li key={i} className="flex gap-3" style={{ color: PALETTE.text }}>
                <span className="shrink-0 mt-2 h-1.5 w-1.5 rounded-full"
                      style={{ background: PALETTE.primary }} />
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>
      <div className="flex justify-between gap-3">
        <button
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0}
          data-testid="vip-slide-prev"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border disabled:opacity-40 transition-all"
          style={{ background: "white", color: PALETTE.textSoft, borderColor: PALETTE.border }}
        >
          <ChevronLeft className="h-4 w-4" /> {t.slide_prev}
        </button>
        <button
          onClick={handleNext}
          data-testid="vip-slide-next"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all"
          style={{ background: PALETTE.primary }}
        >
          {last ? t.mark_slides : t.slide_next}
          {!last ? <ChevronRight className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

// ------- QUIZ TAB -------
function QuizTab({ lesson, t, onSubmit }) {
  const questions = lesson.questions || [];
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);

  if (!questions.length) return null;
  const q = questions[idx];
  const last = idx === questions.length - 1;
  const correct = picked === q.correct_index;

  const handleConfirm = () => {
    if (picked === null || revealed) return;
    setRevealed(true);
    setAnswers([...answers, { qid: q.question_id, picked, correct }]);
  };
  const handleNext = () => {
    if (last) {
      const finalCount = answers.filter((a) => a.correct).length;
      setDone(true);
      onSubmit({ correct: finalCount, total: questions.length });
    } else {
      setIdx(idx + 1); setPicked(null); setRevealed(false);
    }
  };
  const handleRetry = () => {
    setIdx(0); setPicked(null); setRevealed(false); setAnswers([]); setDone(false);
  };

  if (done) {
    const score = answers.filter((a) => a.correct).length;
    const pct = Math.round((score / questions.length) * 100);
    // Cross-link: when the lesson is tagged with oral_topics_linked, surface a
    // big "Pratica all'orale" CTA that jumps to the oral simulator with the
    // topic pre-selected (revenue-oriented integration premium feature).
    const oralTopics = lesson?.oral_topics_linked || [];
    return (
      <div data-testid="vip-quiz-done" className="rounded-2xl p-8 text-center shadow-lg"
           style={{ background: `linear-gradient(135deg, ${PALETTE.primarySoft}, white)`,
                    border: `1px solid ${PALETTE.border}` }}>
        <Sparkles className="h-9 w-9 mx-auto mb-3" style={{ color: PALETTE.primary }} />
        <h3 className="font-serif text-2xl mb-2" style={{ color: PALETTE.primaryDark }}>
          {t.quiz_done}
        </h3>
        <p className="text-sm mb-5" style={{ color: PALETTE.textSoft }}>
          {t.score_label}: <strong style={{ color: PALETTE.text }}>{score}/{questions.length}</strong> ({pct}%)
        </p>
        {oralTopics.length > 0 && (
          <a href={`/vip/oral?topics=${encodeURIComponent(oralTopics.join(","))}&from=lesson`}
             data-testid="vip-quiz-practice-oral"
             className="block mb-3 mx-auto max-w-md rounded-2xl px-5 py-4 text-white font-semibold shadow-lg transition-all hover:scale-[1.02]"
             style={{
               background: "linear-gradient(135deg, #991B1B 0%, #DC2626 50%, #B8862F 100%)",
               boxShadow: "0 10px 28px -10px rgba(220,38,38,0.55)",
             }}>
            <div className="flex items-center justify-center gap-2">
              <Mic className="h-5 w-5" />
              <span className="font-serif text-lg">{t.practice_oral}</span>
            </div>
            <p className="text-xs mt-1 opacity-90">{t.practice_oral_hint}</p>
          </a>
        )}
        <button onClick={handleRetry}
                data-testid="vip-quiz-retry"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border"
                style={{ background: "white", borderColor: PALETTE.border, color: PALETTE.primaryDark }}>
          <RotateCcw className="h-4 w-4" /> {t.retry}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="vip-tab-content-quiz" className="space-y-4">
      <p className="text-xs uppercase tracking-[0.18em]" style={{ color: PALETTE.textSoft }}>
        {idx + 1} / {questions.length}
      </p>
      <h3 className="font-serif text-xl sm:text-2xl leading-snug" style={{ color: PALETTE.text }}>
        {q.stem}
      </h3>
      <div className="space-y-2.5">
        {(q.options || []).map((opt, i) => {
          const chosen = picked === i;
          const isAnswer = i === q.correct_index;
          let bg = "white", color = PALETTE.text, border = PALETTE.border;
          if (revealed) {
            if (isAnswer) { bg = "#E8F5EE"; border = PALETTE.success; color = "#1F6B3A"; }
            else if (chosen && !isAnswer) { bg = "#FCEBEF"; border = PALETTE.danger; color = "#9E2A48"; }
          } else if (chosen) {
            bg = PALETTE.primarySoft; border = PALETTE.primary; color = PALETTE.primaryDark;
          }
          return (
            <button
              key={i}
              data-testid={`vip-quiz-opt-${i}`}
              onClick={() => !revealed && setPicked(i)}
              disabled={revealed}
              className="w-full text-left rounded-xl px-4 py-3 border transition-all flex items-center gap-3"
              style={{ background: bg, color, borderColor: border, cursor: revealed ? "default" : "pointer" }}
            >
              <span className="shrink-0 h-6 w-6 rounded-full grid place-items-center text-xs font-bold"
                    style={{ background: chosen || (revealed && isAnswer) ? border : PALETTE.border,
                             color: chosen || (revealed && isAnswer) ? "white" : PALETTE.textSoft }}>
                {String.fromCharCode(65 + i)}
              </span>
              <span className="flex-1">{opt}</span>
              {revealed && isAnswer ? <CheckCircle2 className="h-4 w-4" style={{ color: PALETTE.success }} /> : null}
              {revealed && chosen && !isAnswer ? <XCircle className="h-4 w-4" style={{ color: PALETTE.danger }} /> : null}
            </button>
          );
        })}
      </div>
      {revealed ? (
        <div data-testid="vip-quiz-explanation"
             className="rounded-xl p-4 text-sm leading-relaxed"
             style={{ background: PALETTE.primarySoft, color: PALETTE.text }}>
          <p className="text-[11px] uppercase tracking-[0.2em] mb-1.5" style={{ color: PALETTE.primary }}>
            {t.explanation}
          </p>
          {q.explanation}
        </div>
      ) : null}
      <div className="pt-2 flex justify-end">
        {!revealed ? (
          <button
            onClick={handleConfirm}
            disabled={picked === null}
            data-testid="vip-quiz-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all disabled:opacity-40"
            style={{ background: PALETTE.primary, cursor: picked === null ? "not-allowed" : "pointer" }}
          >
            {t.submit}
          </button>
        ) : (
          <button
            onClick={handleNext}
            data-testid="vip-quiz-next"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all"
            style={{ background: PALETTE.primary }}
          >
            {last ? t.finish_quiz : t.next_question} <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ------- MAIN PAGE -------
import { TrialOfferModal } from "../components/TrialOfferModal";

export default function VIPLesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("read");
  const [tabSetByUser, setTabSetByUser] = useState(false);
  const [language, setLanguage] = useState("it");
  const [trialTrigger, setTrialTrigger] = useState(null);

  // Auto-select the most useful default tab when a new lesson loads, unless
  // the user has explicitly switched tabs themselves. Video first when a
  // recording exists, otherwise the text-only "Leggi" tab.
  useEffect(() => {
    if (!lesson || tabSetByUser) return;
    setTab(lesson.video ? "video" : "read");
  }, [lesson, tabSetByUser]);
  // Reset the user-override flag when navigating to a different lesson.
  useEffect(() => {
    setTabSetByUser(false);
  }, [lessonId]);
  const handleTabChange = (next) => { setTabSetByUser(true); setTab(next); };

  const refresh = async () => {
    try {
      const data = await apiGet(`/vip/lessons/${lessonId}`);
      setLesson(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    }
  };

  useEffect(() => {
    let alive = true;
    apiGet("/vip/me").then((m) => { if (alive) setLanguage(m.preferred_language || "it"); }).catch(() => {});
    refresh();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [lessonId]);

  const t = UI[language] || UI.it;

  const completionPct = useMemo(() => {
    if (!lesson) return 0;
    let n = 0;
    if (lesson.progress?.read) n++;
    if (lesson.progress?.slides_done) n++;
    if (lesson.progress?.questions_total > 0) n++;
    return Math.round((n / 3) * 100);
  }, [lesson]);

  const audioUrl = useMemo(() => {
    if (!lesson?.audio_url) return null;
    return `${API}${lesson.audio_url.replace(/^\/api/, "")}`;
  }, [lesson?.audio_url]);

  // Trial offer triggers — see TrialOfferModal for session gating logic.
  // - Trigger A (l1_complete): free-preview lesson AND user has touched any
  //   completion field (read / slides_done / quiz submitted).
  // - Trigger B (locked_lesson): user landed on a teaser page for a locked
  //   lesson (L2-L5).
  useEffect(() => {
    if (!lesson) return;
    if (lesson.locked) {
      setTrialTrigger({ reason: "locked_lesson", lessonId: lesson.lesson_id });
      return;
    }
    if (lesson.is_free_preview) {
      const p = lesson.progress || {};
      const completed = p.read || p.slides_done || p.questions_total > 0;
      if (completed) {
        setTrialTrigger({ reason: "l1_complete", lessonId: lesson.lesson_id });
      }
    }
  }, [lesson]);

  if (error) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6"
           data-testid="vip-lesson-error" style={{ color: PALETTE.text }}>
        <div className="text-center">
          <XCircle className="h-9 w-9 mx-auto mb-3" style={{ color: PALETTE.danger }} />
          <p>{t.not_found}</p>
          <Link to="/vip" className="inline-block mt-4 text-sm underline" style={{ color: PALETTE.primary }}>
            {t.back}
          </Link>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="vip-lesson-loading">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: PALETTE.primary }} />
      </div>
    );
  }

  // New CMS block-based lessons render with their own dedicated view.
  if (lesson.format === "blocks" && !lesson.locked) {
    return (
      <>
        <TrialOfferModal language={language} trigger={trialTrigger} />
        <BlocksLessonView lesson={lesson} refresh={refresh} />
      </>
    );
  }

  // Locked state: show a paywall over the lesson teaser (title + summary +
  // first slide title only). No body, no audio, no quiz.
  if (lesson.locked) {
    return (
      <>
      <TrialOfferModal language={language} trigger={trialTrigger} />
      <div data-testid="vip-lesson-locked"
           className="min-h-screen px-4 sm:px-8 py-8" style={{ background: PALETTE.bg }}>
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate("/vip")}
                  data-testid="vip-lesson-back"
                  className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl mb-6"
                  style={{ color: PALETTE.primaryDark, background: "rgba(255,255,255,0.7)" }}>
            <ArrowLeft className="h-4 w-4" /> {t.back}
          </button>
          <p className="text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: PALETTE.primary }}>
            {t.lesson} · Anteprima
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight"
              data-testid="vip-lesson-title" style={{ color: PALETTE.text }}>
            {lesson.title}
          </h1>
          {lesson.summary ? (
            <p className="text-lg mb-8 leading-relaxed" style={{ color: PALETTE.textSoft }}>
              {lesson.summary}
            </p>
          ) : null}
          <div className="rounded-2xl p-8 text-center shadow-lg"
               style={{ background: `linear-gradient(135deg, white 0%, ${PALETTE.primarySoft} 100%)`,
                        border: `1px solid ${PALETTE.border}` }}>
            <div className="h-14 w-14 rounded-full grid place-items-center mx-auto mb-4"
                 style={{ background: PALETTE.primary }}>
              <CircleHelp className="h-7 w-7 text-white" strokeWidth={1.6} />
            </div>
            <h2 className="font-serif text-2xl mb-2" style={{ color: PALETTE.primaryDark }}>
              Contenuto bloccato
            </h2>
            <p className="text-sm mb-6 max-w-md mx-auto leading-relaxed" style={{ color: PALETTE.textSoft }}>
              Questa lezione fa parte dell'Academia Médicos para Italia.
              Acquista l'accesso completo per sbloccare audio, slide e quiz.
            </p>
            <Link to="/vip"
                  data-testid="vip-lesson-buy-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium"
                  style={{ background: PALETTE.primary }}>
              Sblocca ora
            </Link>
          </div>
        </div>
      </div>
      </>
    );
  }

  const updateField = async (field, value) => {
    try {
      await apiPost(`/vip/lessons/${lessonId}/progress`, { field, value });
      refresh();
    } catch {}
  };

  const handleLanguageChange = async (next) => {
    if (next === language) return;
    const prev = language;
    setLanguage(next);
    try {
      await apiPost("/vip/language", { language: next });
      // Refetch the lesson so the backend re-emits audio/video URLs and
      // bilingual fields for the freshly selected language.
      await refresh();
    } catch {
      setLanguage(prev);
    }
  };

  return (
    <>
    <TrialOfferModal language={language} trigger={trialTrigger} />
    <div data-testid="vip-lesson-page"
         className="min-h-screen px-4 sm:px-8 py-8"
         style={{ background: PALETTE.bg }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <button onClick={() => navigate("/vip")}
                  data-testid="vip-lesson-back"
                  className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl transition-colors"
                  style={{ color: PALETTE.primaryDark, background: "rgba(255,255,255,0.7)" }}>
            <ArrowLeft className="h-4 w-4" /> {t.back}
          </button>
          {/* Compact IT/ES toggle — segmented control. Switching refetches
              the lesson so audio/video/text all flip to the new language. */}
          <div className="inline-flex items-center rounded-xl overflow-hidden shadow-sm shrink-0"
               role="group"
               aria-label={t.lang_toggle_label}
               data-testid="vip-lesson-lang-toggle"
               style={{ background: "rgba(255,255,255,0.7)",
                        border: `1px solid ${PALETTE.border}` }}>
            {[
              { v: "it", label: "IT" },
              { v: "es", label: "ES" },
            ].map((opt) => {
              const active = language === opt.v;
              return (
                <button
                  key={opt.v}
                  onClick={() => handleLanguageChange(opt.v)}
                  data-testid={`vip-lesson-lang-${opt.v}`}
                  aria-pressed={active}
                  className="px-3 py-2 text-xs font-semibold tracking-wider uppercase transition-colors"
                  style={{
                    background: active ? PALETTE.primary : "transparent",
                    color: active ? "white" : PALETTE.primaryDark,
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Progress dial */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium hidden sm:block" style={{ color: PALETTE.textSoft }}>
              {completionPct === 100 ? t.progress_complete : t.progress_keep}
            </span>
            <ProgressRing pct={completionPct} />
          </div>
        </div>

        {/* Title */}
        <p className="text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: PALETTE.primary }}>
          {t.lesson}
          {lesson.lesson_total ? (
            <span className="ml-2 font-mono opacity-75 normal-case tracking-normal">
              · {lesson.lesson_index} {t.lesson_of} {lesson.lesson_total}
            </span>
          ) : null}
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight"
            data-testid="vip-lesson-title" style={{ color: PALETTE.text }}>
          {lesson.title}
        </h1>

        {/* Video moved to its own dedicated tab (see below). Keeps the
            top of the page clean and the "Leggi" tab text-only. */}

        {/* Audio */}
        <div className="mb-6">
          <AudioPlayer src={audioUrl} status={lesson.audio_status}
                       label={lesson.audio_status === "generating"
                         ? t.audio_generating
                         : (audioUrl ? t.audio_label : t.audio_unavailable)} />
        </div>

        {/* Tabs — Video is shown FIRST as the primary lesson content. The
            tab always renders, even when no video has been published yet,
            so the navigation layout stays consistent across lessons. */}
        <Tabs value={tab} onChange={handleTabChange} items={[
          { value: "video", label: t.tab_video, icon: Film },
          { value: "read", label: t.tab_read, icon: BookOpen },
          { value: "slides", label: t.tab_slides, icon: Presentation },
          { value: "quiz", label: t.tab_quiz, icon: CircleHelp },
        ]} />

        <div className="rounded-3xl p-6 sm:p-8 shadow-sm"
             style={{ background: PALETTE.card, border: `1px solid ${PALETTE.border}` }}>
          {tab === "video" && <VideoTab lesson={{ ...lesson, __lang: language }} t={t} language={language} />}
          {tab === "read" && <ReadTab lesson={lesson} t={t}
                                      onMarkRead={() => updateField("read", true)} />}
          {tab === "slides" && <SlidesTab lesson={lesson} t={t}
                                          onMarkSlidesDone={() => updateField("slides_done", true)} />}
          {tab === "quiz" && <QuizTab lesson={lesson} t={t}
                                      onSubmit={(s) => updateField("questions_score", s)} />}
        </div>

        {/* Prev / Next lesson nav */}
        {(lesson.prev_lesson || lesson.next_lesson) ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
               data-testid="vip-lesson-nav">
            {lesson.prev_lesson ? (
              <Link to={`/vip/lessons/${lesson.prev_lesson.lesson_id}`}
                    data-testid="vip-prev-lesson"
                    className="group flex items-center gap-3 px-5 py-4 rounded-2xl border transition-all hover:shadow-md"
                    style={{ borderColor: PALETTE.border, background: PALETTE.card }}>
                <ChevronLeft className="h-5 w-5 shrink-0 transition-transform group-hover:-translate-x-0.5"
                             style={{ color: PALETTE.primary }} />
                <div className="min-w-0 text-left">
                  <p className="text-[10px] uppercase tracking-[0.2em] mb-0.5"
                     style={{ color: PALETTE.textSoft }}>
                    {t.prev_lesson}
                  </p>
                  <p className="text-sm leading-snug line-clamp-2"
                     style={{ color: PALETTE.text }}>
                    {lesson.prev_lesson.title}
                  </p>
                </div>
              </Link>
            ) : <span aria-hidden />}
            {lesson.next_lesson ? (
              <Link to={`/vip/lessons/${lesson.next_lesson.lesson_id}`}
                    data-testid="vip-next-lesson"
                    className="group flex items-center gap-3 px-5 py-4 rounded-2xl border transition-all hover:shadow-md sm:flex-row-reverse sm:text-right"
                    style={{ borderColor: PALETTE.border, background: PALETTE.card }}>
                <ChevronRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5"
                              style={{ color: PALETTE.primary }} />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] mb-0.5"
                     style={{ color: PALETTE.textSoft }}>
                    {t.next_lesson}
                  </p>
                  <p className="text-sm leading-snug line-clamp-2"
                     style={{ color: PALETTE.text }}>
                    {lesson.next_lesson.title}
                  </p>
                </div>
              </Link>
            ) : <span aria-hidden />}
          </div>
        ) : null}

        {/* Upgrade CTA — visible only when this lesson is a free preview
            AND the user is on FREE tier (no academy access). When backend
            returns is_free_preview but suppresses next_lesson, that's the
            signal to convert the user. */}
        {lesson.is_free_preview && !lesson.next_lesson ? (
          <UpgradeBanner t={t} />
        ) : null}
      </div>
    </div>
    </>
  );
}

function UpgradeBanner({ t }) {
  return (
    <div data-testid="vip-upgrade-banner"
         className="mt-10 rounded-3xl p-7 sm:p-9 text-center shadow-xl"
         style={{
           background: "linear-gradient(135deg, #6B4DD8 0%, #4A2EAA 100%)",
           color: "#FFFFFF",
         }}>
      <div className="h-14 w-14 rounded-full grid place-items-center mx-auto mb-4"
           style={{ background: "rgba(255,255,255,0.18)" }}>
        <Sparkles className="h-7 w-7" strokeWidth={1.6} />
      </div>
      <p className="text-[11px] uppercase tracking-[0.22em] mb-2 opacity-85">
        {t.upgrade_eyebrow}
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl mb-3 leading-tight">
        {t.upgrade_title}
      </h2>
      <p className="text-base mb-6 max-w-xl mx-auto leading-relaxed opacity-90">
        {t.upgrade_desc}
      </p>
      <Link to="/vip"
            data-testid="vip-upgrade-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-base font-medium transition-all hover:scale-[1.02]"
            style={{ background: "#FFFFFF", color: "#4A2EAA" }}>
        <Sparkles className="h-4 w-4" /> {t.upgrade_cta}
      </Link>
    </div>
  );
}

function ProgressRing({ pct }) {
  const r = 18, c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" data-testid="vip-progress-ring">
      <circle cx="22" cy="22" r={r} fill="none" stroke={PALETTE.border} strokeWidth="4" />
      <circle cx="22" cy="22" r={r} fill="none" stroke={PALETTE.primary} strokeWidth="4"
              strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
              transform="rotate(-90 22 22)"
              style={{ transition: "stroke-dashoffset 0.4s ease" }} />
      <text x="22" y="26" textAnchor="middle" fontSize="11" fontWeight="600"
            fill={PALETTE.primaryDark}>{pct}%</text>
    </svg>
  );
}
